import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { UserCircle, Video, Gift } from 'lucide-react';
import { AgeVerification } from '@/components/legal/AgeVerification';
import { useReferral } from '@/hooks/useReferral';

type UserRole = 'fan' | 'creator';

export default function Auth() {
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref');
  const { applyReferralCode } = useReferral();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<UserRole>('fan');
  const [referralCode, setReferralCode] = useState(refParam || '');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, userRole } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (user && userRole) {
      navigate(userRole === 'creator' ? '/creator' : '/fan');
    }
  }, [user, userRole, navigate]);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email }
      });

      if (error) throw error;
      
      toast.success('Password reset email sent! Check your inbox.');
      setIsForgotPassword(false);
      setEmail('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        
        // Fetch user role to redirect appropriately
        const { data: roleData } = await supabase.rpc('get_user_role', {
          _user_id: data.user.id
        });
        
        toast.success('Successfully logged in!');
        navigate(roleData === 'creator' ? '/creator' : '/fan');
      } else {
        const redirectUrl = `${window.location.origin}/${role === 'creator' ? 'creator' : 'fan'}`;
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              role: role,
              username: username,
            }
          },
        });

        if (error) throw error;
        toast.success('Account created! You can now sign in.');
        
        // Apply referral code if provided
        if (referralCode.trim()) {
          // Wait a bit for the user to be fully created
          setTimeout(async () => {
            await applyReferralCode(referralCode.trim());
          }, 1000);
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 gradient-stage opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background" />
        <div className="absolute top-20 left-20 h-96 w-96 rounded-full gradient-stage opacity-20 blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 right-20 h-96 w-96 rounded-full gradient-spotlight opacity-20 blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
      </div>

      <Card className="w-full max-w-md glass-card border-border/50 shadow-2xl">
        <CardHeader className="text-center space-y-2 pb-6">
          <div className="text-3xl font-bold gradient-stage bg-clip-text text-transparent mb-2">
            HiStage
          </div>
          <CardTitle className="text-2xl font-bold">
            {isForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Join HiStage'}
          </CardTitle>
          <CardDescription className="text-base">
            {isForgotPassword 
              ? 'Enter your email to receive a password reset link'
              : isLogin
              ? 'Sign in to access your account'
              : 'Create your account and start streaming'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-sm font-medium">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 bg-background/50"
                />
              </div>
              <Button 
                type="submit" 
                variant="default"
                className="w-full h-11 text-base font-semibold shadow-lg rounded-full" 
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setEmail('');
                }}
                className="w-full text-center text-sm text-primary hover:underline font-medium"
              >
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 bg-background/50"
                />
              </div>
              
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-sm font-medium">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="johndoe"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    required
                    minLength={3}
                    maxLength={20}
                    pattern="[a-z0-9_]+"
                    className="h-11 bg-background/50"
                  />
                  <p className="text-xs text-muted-foreground">
                    3-20 characters, lowercase letters, numbers, and underscores only
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setIsForgotPassword(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-11 bg-background/50"
                />
                {!isLogin && (
                  <p className="text-xs text-muted-foreground">
                    Minimum 6 characters
                  </p>
                )}
              </div>
              
              {!isLogin && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">I want to join as</Label>
                  <RadioGroup value={role} onValueChange={(value) => setRole(value as UserRole)}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <RadioGroupItem value="fan" id="fan" className="peer sr-only" />
                        <Label
                          htmlFor="fan"
                          className="flex flex-col items-center justify-between rounded-lg border-2 border-border bg-background/50 p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                        >
                          <UserCircle className="mb-2 h-6 w-6" />
                          <div className="text-center">
                            <div className="font-semibold">Fan</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Watch & support creators
                            </div>
                          </div>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="creator" id="creator" className="peer sr-only" />
                        <Label
                          htmlFor="creator"
                          className="flex flex-col items-center justify-between rounded-lg border-2 border-border bg-background/50 p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                        >
                          <Video className="mb-2 h-6 w-6" />
                          <div className="text-center">
                            <div className="font-semibold">Creator</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Stream & earn
                            </div>
                          </div>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              )}
              
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="referral" className="text-sm font-medium flex items-center gap-2">
                    <Gift className="h-4 w-4 text-primary" />
                    Referral Code (Optional)
                  </Label>
                  <Input
                    id="referral"
                    type="text"
                    placeholder="ABC12345"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    maxLength={8}
                    className="h-11 bg-background/50 font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Have a referral code? Your referrer will earn 1% from your spending for 3 months!
                  </p>
                </div>
              )}
              <Button
                type="submit" 
                variant="default"
                className="w-full h-11 text-base font-semibold shadow-lg rounded-full" 
                disabled={loading}
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
          )}
          
          {!isForgotPassword && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/50" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    {isLogin ? 'New to HiStage?' : 'Already have an account?'}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="w-full text-center text-sm text-primary hover:underline font-medium"
              >
                {isLogin
                  ? 'Create a new account'
                  : 'Sign in to your account'}
              </button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
